'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, Shield, Lock, Eye, CheckCircle, Code, AlertTriangle, Users, Database, Globe, Zap, FileText } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const cybersecurityData = {
  title: "Cybersecurity",
  subtitle: "Protect your digital assets with comprehensive security solutions that safeguard against threats, ensure compliance, and maintain business continuity in an increasingly connected world",
  headerGradient: "bg-gradient-to-r from-slate-800 via-gray-700 to-zinc-700",
  accentColor: "text-slate-700",
  buttonColor: "bg-slate-700 hover:bg-slate-800",
  
  overview: "Cybersecurity services provide comprehensive protection for your digital infrastructure, data, and operations. Our security solutions include threat detection and response, vulnerability assessments, compliance management, security architecture design, and incident response planning. We help organizations build robust security postures that protect against evolving cyber threats while enabling secure digital transformation and business growth.",
  
  capabilities: [
    {
      name: "Threat Detection & Response",
      description: "Advanced monitoring and rapid response to identify, analyze, and neutralize security threats in real-time",
      icon: Eye,
      applications: ["SIEM Systems", "SOC Operations", "Incident Response", "Threat Intelligence"],
      useCases: ["Real-time monitoring", "Automated response", "Forensic analysis", "Threat hunting"]
    },
    {
      name: "Security Architecture",
      description: "Design and implement comprehensive security frameworks that protect your entire digital ecosystem",
      icon: Shield,
      applications: ["Zero Trust Architecture", "Network Security", "Identity Management", "Access Controls"],
      useCases: ["Secure design", "Risk mitigation", "Compliance alignment", "Defense in depth"]
    },
    {
      name: "Compliance & Governance",
      description: "Ensure adherence to regulatory requirements and industry standards while maintaining operational efficiency",
      icon: FileText,
      applications: ["GDPR Compliance", "SOX Compliance", "HIPAA Compliance", "ISO 27001"],
      useCases: ["Regulatory compliance", "Audit preparation", "Policy development", "Risk assessment"]
    }
  ],
  
  technologies: [
    {
      category: "Security Platforms",
      tools: ["Splunk", "IBM QRadar", "Microsoft Sentinel", "CrowdStrike", "Palo Alto Networks", "Fortinet"],
      description: "Enterprise security information and event management platforms"
    },
    {
      category: "Vulnerability Management",
      tools: ["Nessus", "Qualys", "Rapid7", "OpenVAS", "Burp Suite", "OWASP ZAP"],
      description: "Tools for identifying and managing security vulnerabilities"
    },
    {
      category: "Identity & Access Management",
      tools: ["Okta", "Azure AD", "Ping Identity", "CyberArk", "HashiCorp Vault", "Auth0"],
      description: "Identity management and access control solutions"
    },
    {
      category: "Network Security",
      tools: ["Cisco ASA", "pfSense", "Wireshark", "Nmap", "Metasploit", "Snort"],
      description: "Network monitoring, analysis, and protection tools"
    },
    {
      category: "Encryption & PKI",
      tools: ["Let's Encrypt", "DigiCert", "AWS KMS", "Azure Key Vault", "HashiCorp Vault", "OpenSSL"],
      description: "Encryption and public key infrastructure solutions"
    },
    {
      category: "Compliance Tools",
      tools: ["Nessus", "Rapid7", "Qualys", "Tenable", "RSA Archer", "ServiceNow GRC"],
      description: "Governance, risk, and compliance management platforms"
    }
  ],
  
  securityDomains: [
    {
      domain: "Application Security",
      description: "Secure software development and application protection",
      examples: ["SAST/DAST Testing", "Code Review", "API Security", "Web Application Firewalls", "Container Security", "DevSecOps"],
      useCases: ["Secure coding", "Vulnerability testing", "Runtime protection", "Supply chain security"]
    },
    {
      domain: "Infrastructure Security",
      description: "Protect physical and virtual infrastructure components",
      examples: ["Network Segmentation", "Endpoint Protection", "Server Hardening", "Cloud Security", "IoT Security", "Industrial Control Systems"],
      useCases: ["Perimeter defense", "Asset protection", "Configuration management", "Monitoring"]
    },
    {
      domain: "Data Security",
      description: "Protect sensitive data throughout its lifecycle",
      examples: ["Data Encryption", "Data Loss Prevention", "Database Security", "Backup Security", "Data Classification", "Privacy Protection"],
      useCases: ["Data protection", "Privacy compliance", "Secure storage", "Access control"]
    },
    {
      domain: "Identity Security",
      description: "Manage and secure user identities and access rights",
      examples: ["Multi-Factor Authentication", "Single Sign-On", "Privileged Access Management", "Identity Governance", "Behavioral Analytics", "Zero Trust"],
      useCases: ["Access management", "Identity verification", "Privilege control", "User monitoring"]
    }
  ],
  
  industries: [
    {
      name: "Financial Services",
      applications: ["Fraud detection", "Transaction security", "Regulatory compliance", "Customer data protection"],
      impact: "99.9% fraud prevention accuracy, full regulatory compliance"
    },
    {
      name: "Healthcare",
      applications: ["Patient data protection", "HIPAA compliance", "Medical device security", "Telemedicine security"],
      impact: "Zero data breaches, 100% HIPAA compliance maintained"
    },
    {
      name: "Government",
      applications: ["Classified data protection", "Citizen privacy", "Critical infrastructure", "Cyber warfare defense"],
      impact: "National security maintained, citizen trust preserved"
    },
    {
      name: "Manufacturing",
      applications: ["Industrial control systems", "Supply chain security", "Intellectual property protection", "Operational technology"],
      impact: "Zero production disruptions, IP theft prevention"
    },
    {
      name: "Retail & E-commerce",
      applications: ["Payment security", "Customer data protection", "PCI DSS compliance", "Supply chain security"],
      impact: "Secure transactions, customer trust maintained"
    },
    {
      name: "Education",
      applications: ["Student data protection", "Research security", "Campus network security", "Remote learning security"],
      impact: "Student privacy protected, research integrity maintained"
    }
  ],
  
  threatLandscape: [
    {
      threat: "Advanced Persistent Threats (APTs)",
      description: "Sophisticated, long-term attacks targeting specific organizations",
      mitigation: "Continuous monitoring, threat intelligence, behavioral analytics, and incident response",
      impact: "Data theft, espionage, operational disruption"
    },
    {
      threat: "Ransomware",
      description: "Malicious software that encrypts data and demands payment for decryption",
      mitigation: "Backup strategies, endpoint protection, user training, and incident response planning",
      impact: "Business disruption, financial loss, reputation damage"
    },
    {
      threat: "Social Engineering",
      description: "Psychological manipulation to trick users into revealing sensitive information",
      mitigation: "Security awareness training, phishing simulation, and access controls",
      impact: "Credential theft, unauthorized access, data breaches"
    },
    {
      threat: "Supply Chain Attacks",
      description: "Compromising third-party vendors to gain access to target organizations",
      mitigation: "Vendor risk assessment, supply chain monitoring, and zero trust architecture",
      impact: "Widespread compromise, data theft, operational disruption"
    }
  ],
  
  securityFrameworks: [
    {
      framework: "NIST Cybersecurity Framework",
      description: "Comprehensive framework for managing cybersecurity risk",
      components: ["Identify", "Protect", "Detect", "Respond", "Recover"],
      benefits: ["Risk-based approach", "Industry agnostic", "Flexible implementation", "Continuous improvement"]
    },
    {
      framework: "ISO 27001",
      description: "International standard for information security management systems",
      components: ["Risk Assessment", "Security Controls", "Management System", "Continuous Monitoring"],
      benefits: ["Global recognition", "Systematic approach", "Legal compliance", "Customer confidence"]
    },
    {
      framework: "Zero Trust Architecture",
      description: "Security model that requires verification for every user and device",
      components: ["Identity Verification", "Device Trust", "Network Segmentation", "Data Protection"],
      benefits: ["Reduced attack surface", "Improved visibility", "Enhanced security", "Flexible access"]
    },
    {
      framework: "MITRE ATT&CK",
      description: "Knowledge base of adversary tactics and techniques",
      components: ["Tactics", "Techniques", "Procedures", "Mitigations"],
      benefits: ["Threat understanding", "Defense planning", "Security testing", "Incident analysis"]
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Security Assessment & Risk Analysis",
      description: "Comprehensive evaluation of current security posture and risk landscape",
      details: ["Vulnerability assessment", "Penetration testing", "Risk analysis", "Compliance gap analysis"]
    },
    {
      step: "2", 
      title: "Security Strategy & Architecture Design",
      description: "Develop comprehensive security strategy and architecture framework",
      details: ["Security roadmap", "Architecture design", "Policy development", "Technology selection"]
    },
    {
      step: "3",
      title: "Implementation & Deployment",
      description: "Deploy security controls, tools, and processes across the organization",
      details: ["Security tool deployment", "Process implementation", "Training delivery", "Integration testing"]
    },
    {
      step: "4",
      title: "Monitoring & Detection",
      description: "Establish continuous monitoring and threat detection capabilities",
      details: ["SIEM deployment", "SOC setup", "Threat intelligence", "Incident response procedures"]
    },
    {
      step: "5",
      title: "Incident Response & Recovery",
      description: "Rapid response to security incidents and business continuity planning",
      details: ["Incident handling", "Forensic analysis", "Recovery procedures", "Lessons learned"]
    },
    {
      step: "6",
      title: "Continuous Improvement",
      description: "Ongoing security optimization and adaptation to emerging threats",
      details: ["Security metrics", "Regular assessments", "Technology updates", "Training programs"]
    }
  ],
  
  benefits: [
    "Comprehensive protection against cyber threats",
    "Regulatory compliance and risk reduction",
    "Business continuity and operational resilience",
    "Customer trust and brand protection",
    "Reduced security incidents by 90%",
    "Faster incident response and recovery",
    "Cost-effective security investment",
    "Competitive advantage through security"
  ],
  
  challenges: [
    {
      challenge: "Evolving Threat Landscape",
      solution: "Continuous threat intelligence, adaptive security controls, and proactive monitoring",
      description: "Stay ahead of emerging threats with dynamic security measures"
    },
    {
      challenge: "Skills Shortage",
      solution: "Managed security services, automation, and comprehensive training programs",
      description: "Address cybersecurity talent gaps with expert services and automation"
    },
    {
      challenge: "Compliance Complexity",
      solution: "Automated compliance monitoring, expert guidance, and integrated frameworks",
      description: "Simplify compliance management with automated tools and expert support"
    },
    {
      challenge: "Budget Constraints",
      solution: "Risk-based prioritization, cost-effective solutions, and phased implementation",
      description: "Maximize security ROI with strategic planning and efficient resource allocation"
    }
  ]
};

export default function CybersecurityPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${cybersecurityData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px),
                             radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 60px 60px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(cybersecurityData.title)}
              </h1>
              <p className="text-xl text-slate-200 mb-8 leading-relaxed">
                {localize(cybersecurityData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["Threat Detection", "Compliance", "Risk Management", "Incident Response"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <div className="text-2xl font-bold">99.9%</div>
                    <div className="text-sm text-slate-200">{localize("Threat Prevention")}</div>
                  </div>
                  <div className="text-center">
                    <Zap className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-slate-200">{localize("Monitoring")}</div>
                  </div>
                  <div className="text-center">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <div className="text-2xl font-bold">Enterprise</div>
                    <div className="text-sm text-slate-200">{localize("Grade Security")}</div>
                  </div>
                  <div className="text-center">
                    <Globe className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <div className="text-2xl font-bold">Global</div>
                    <div className="text-sm text-slate-200">{localize("Compliance")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${cybersecurityData.accentColor}`}>
              {localize("What is Cybersecurity?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(cybersecurityData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core Cybersecurity Capabilities")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {cybersecurityData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-slate-600">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-slate-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-slate-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Security Domains Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Security Domains & Protection Areas")}
          </motion.h2>
          
          <div className="space-y-8">
            {cybersecurityData.securityDomains.map((domain, index) => (
              <motion.div key={domain.domain} variants={itemVariants}>
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <div className="w-8 h-8 bg-slate-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      {localize(domain.domain)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(domain.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Examples")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {domain.examples.map((example) => (
                            <Badge key={example} variant="secondary" className="text-xs">
                              {localize(example)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Common Use Cases")}
                        </h4>
                        <ul className="space-y-2">
                          {domain.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-slate-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Security Frameworks Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Security Frameworks & Standards")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {cybersecurityData.securityFrameworks.map((framework, index) => (
              <motion.div key={framework.framework} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(framework.framework)}</CardTitle>
                    <CardDescription className="text-base">{localize(framework.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Components")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {framework.components.map((component) => (
                            <Badge key={component} variant="outline" className="text-xs">
                              {localize(component)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Benefits")}
                        </h4>
                        <ul className="space-y-1">
                          {framework.benefits.map((benefit) => (
                            <li key={benefit} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-slate-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(benefit)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Cybersecurity Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cybersecurityData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-slate-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Threat Landscape */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Current Threat Landscape & Mitigation")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {cybersecurityData.threatLandscape.map((item, index) => (
              <motion.div key={item.threat} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      {localize(item.threat)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h4 className="font-semibold text-red-800 mb-2">{localize("Potential Impact")}</h4>
                        <p className="text-sm text-red-700">{localize(item.impact)}</p>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h4 className="font-semibold text-slate-800 mb-2">{localize("Our Mitigation Strategy")}</h4>
                        <p className="text-sm text-slate-700">{localize(item.mitigation)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cybersecurityData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-slate-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our Cybersecurity Implementation Process")}
          </h2>
          
          <div className="space-y-8">
            {cybersecurityData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-slate-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Security Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Cybersecurity Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {cybersecurityData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-slate-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-slate-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 md:p-12 border border-slate-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose Cybersecurity?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {cybersecurityData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Secure Your Digital Assets?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our cybersecurity experts help you build a comprehensive security posture that protects against threats and ensures business continuity.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${cybersecurityData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Security Assessment")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 